import { useCallback, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Portal,
  Stack,
  Typography
} from "@mui/material";

// Panel Auto-Refresh - the 30 second countdown notification.
//
// Deliberately a non-blocking card rather than a Modal: the whole point of the
// warning is to let the user finish what they are doing, so it never traps input.
//
// The card starts centered on the page and can be dragged anywhere with the mouse,
// touch or pen. Dragging uses native pointer events, so no extra dependency is
// needed, and the card is clamped inside the viewport so it can never be lost
// off-screen.
export default function RefreshCountdownSnackbar({
  open,
  secondsLeft,
  warningSeconds,
  title,
  body,
  buttonLabel,
  onRefresh
}) {
  const dragState = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [pos, setPos] = useState(null);

  const handlePointerDown = useCallback((event) => {
    if (event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    dragState.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top
    };
    setPos({ x: rect.left, y: rect.top });
    setDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, []);

  const handlePointerMove = useCallback((event) => {
    const state = dragState.current;
    if (!state) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const maxX = Math.max(0, window.innerWidth - rect.width);
    const maxY = Math.max(0, window.innerHeight - rect.height);
    const x = Math.min(Math.max(0, event.clientX - state.offsetX), maxX);
    const y = Math.min(Math.max(0, event.clientY - state.offsetY), maxY);
    setPos({ x, y });
  }, []);

  const handlePointerUp = useCallback((event) => {
    dragState.current = null;
    setDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  if (!open) return null;

  const left = Number(secondsLeft) || 0;
  const total = warningSeconds > 0 ? warningSeconds : 30;
  const percent = Math.min(100, Math.max(0, (left / total) * 100));

  const style = pos
    ? { position: "fixed", left: pos.x, top: pos.y, m: 0 }
    : {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        m: 0
      };

  return (
    <Portal>
      <Paper
        elevation={12}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        sx={{
          ...style,
          zIndex: (theme) => theme.zIndex.snackbar + 100,
          borderTop: "4px solid",
          borderColor: "warning.main",
          bgcolor: "warning.lighter",
          px: 2.5,
          py: 2,
          maxWidth: 620,
          width: "100%",
          cursor: dragging ? "grabbing" : "grab",
          touchAction: "none",
          userSelect: "none",
          boxShadow: (theme) => theme.shadows[12]
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
            <CircularProgress
              variant="determinate"
              value={percent}
              size={54}
              thickness={4}
              color="warning"
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Typography variant="body2" fontWeight={700} color="warning.dark">
                {left}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {String(title || "").replace("{seconds}", left)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {body}
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="warning"
            onClick={onRefresh}
            onPointerDown={(event) => event.stopPropagation()}
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
          >
            {buttonLabel}
          </Button>
        </Stack>
      </Paper>
    </Portal>
  );
}
